// Inline SVG icon set (stroke-based, 1.75px, currentColor) — a small, consistent
// Lucide-style family so the UI uses crisp vector icons instead of emoji (which
// render inconsistently across platforms). Sized in `em` so icons scale with the
// surrounding text; color follows `currentColor`.

import type { SVGProps } from "react";

export type IconName =
  | "tickets"
  | "my-work"
  | "projects"
  | "action-items"
  | "access"
  | "dashboards"
  | "reports"
  | "assets"
  | "settings"
  | "bell"
  | "search"
  | "plus"
  | "user"
  | "logout"
  | "menu"
  | "wrench"
  | "equipment"
  | "kb"
  | "parts"
  | "forms"
  | "target"
  | "ai";

// Each entry is the inner markup of a 24×24 viewBox icon.
const PATHS: Record<IconName, JSX.Element> = {
  tickets: (
    <>
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4Z" />
      <path d="M13 7v10" strokeDasharray="2 2" />
    </>
  ),
  "my-work": (
    <>
      <path d="M21 11.5A8.5 8.5 0 1 1 12.5 3" />
      <path d="m8.5 12 2.5 2.5L21 4.5" />
    </>
  ),
  projects: (
    <>
      <rect x="3" y="4" width="6" height="16" rx="1" />
      <rect x="10" y="4" width="6" height="10" rx="1" />
      <rect x="17" y="4" width="4" height="13" rx="1" />
    </>
  ),
  "action-items": (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="m8 9 2 2 3.5-3.5" />
      <path d="M8 15h8" />
    </>
  ),
  access: (
    <>
      <circle cx="8" cy="8" r="4" />
      <path d="m10.8 10.8 8.2 8.2M16 16l2-2M19 13l1.5 1.5" />
    </>
  ),
  dashboards: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" rx="0.5" />
      <rect x="12" y="7" width="3" height="10" rx="0.5" />
      <rect x="17" y="13" width="3" height="4" rx="0.5" />
    </>
  ),
  reports: (
    <>
      <path d="M3 3v18h18" />
      <path d="m6 14 4-4 3 3 5-6" />
      <path d="M18 7h3v3" />
    </>
  ),
  assets: (
    <>
      <path d="M21 8 12 3 3 8l9 5 9-5Z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>
  ),
  menu: (
    <>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </>
  ),
  // Wrench — Facilities work orders.
  wrench: (
    <>
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.2L3 17.8 6.2 21l6.3-6.3a4 4 0 0 0 5.2-5.4l-2.5 2.5-2.3-.3-.3-2.3 2.6-2.6Z" />
    </>
  ),
  // Equipment / plant registry.
  equipment: (
    <>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 18v3M16 18v3M6 21h12" />
    </>
  ),
  // Knowledge base — open book.
  kb: (
    <>
      <path d="M12 6.5V20" />
      <path d="M12 6.5A4 4 0 0 0 8 4H3v13h5a4 4 0 0 1 4 2.5" />
      <path d="M12 6.5A4 4 0 0 1 16 4h5v13h-5a4 4 0 0 0-4 2.5" />
    </>
  ),
  // Parts — package / box with a tape seam (distinct from the assets cube).
  parts: (
    <>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
      <path d="m7.5 4.3 9 5.2" />
    </>
  ),
  // Forms — clipboard with lines.
  forms: (
    <>
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="M9 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3" />
      <path d="M9 13h6M9 17h4" />
    </>
  ),
  // Goals — target / bullseye.
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  // Sparkles — the conventional "AI / generated" glyph.
  ai: (
    <>
      <path d="M12 3.5 13.8 8.2 18.5 10 13.8 11.8 12 16.5 10.2 11.8 5.5 10 10.2 8.2Z" />
      <path d="M18 15.5 18.8 17.7 21 18.5 18.8 19.3 18 21.5 17.2 19.3 15 18.5 17.2 17.7Z" />
    </>
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  /** Pixel size; defaults to 1.15em so it tracks the text it sits beside. */
  size?: number | string;
}

export function Icon({ name, size = "1.15em", className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className ? `icon ${className}` : "icon"}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
